import { create } from 'zustand';

export interface OrgPluginSetting {
  pluginId: string;
  enabled: boolean;
}

interface OrgPluginState {
  /** Organization-level plugin settings fetched from server. */
  orgPluginSettings: OrgPluginSetting[];
  /** True after the first successful fetch (even if empty). */
  loaded: boolean;

  fetchOrgPluginSettings: (token?: string | null) => Promise<void>;
  saveOrgPluginSetting: (pluginId: string, enabled: boolean, token?: string | null) => Promise<void>;

  /** Returns true if the plugin is enabled at the org level.
   *  If no org settings exist for this plugin, it's considered enabled (backwards compat). */
  isOrgEnabled: (pluginId: string) => boolean;
  /** Returns the set of plugin IDs explicitly disabled by the org admin. */
  getOrgDisabledIds: () => Set<string>;
}

export const useOrgPluginStore = create<OrgPluginState>()((set, get) => ({
  orgPluginSettings: [],
  loaded: false,

  fetchOrgPluginSettings: async (token) => {
    try {
      const headers: HeadersInit = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch('/api/dotnet/organization/plugins/settings', { headers });
      if (!res.ok) return;
      const raw = (await res.json()) as OrgPluginSetting[];
      set({ orgPluginSettings: Array.isArray(raw) ? raw : [], loaded: true });
    } catch {
      // silently ignore — org settings are optional until first save
      set({ loaded: true });
    }
  },

  saveOrgPluginSetting: async (pluginId, enabled, token) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`/api/dotnet/organization/plugins/settings/${pluginId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ enabled }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const updated = (await res.json()) as OrgPluginSetting;
    set((s) => ({
      orgPluginSettings: [
        ...s.orgPluginSettings.filter((x) => x.pluginId !== pluginId),
        updated,
      ],
    }));
  },

  isOrgEnabled: (pluginId) => {
    const setting = get().orgPluginSettings.find((s) => s.pluginId === pluginId);
    // No setting = not explicitly disabled → enabled by default (backwards compat)
    if (!setting) return true;
    return setting.enabled;
  },

  getOrgDisabledIds: () => {
    return new Set(
      get().orgPluginSettings.filter((s) => !s.enabled).map((s) => s.pluginId),
    );
  },
}));
